import './image.scss';

import NextImage, { ImageLoader } from 'next/image';
import React from 'react';
import z from 'zod';

import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { formatDuration } from '@sola/utils/video.js';

import {
  getAspectHeight,
  getAspectWidth,
  getImageVariant,
} from '../../utils/image-utils';

export const ImageSizeIntent = z.enum(['hero', 'full', 'half', 'third', 'og']);
export type ImageSizeIntent = z.infer<typeof ImageSizeIntent>;

// The largest size needed from Contentful to generate other sizes from.
const intentSourceSizes: Record<ImageSizeIntent, string> = {
  hero: '3840',
  full: '1920',
  half: '1200',
  third: '1000',
  og: '800',
};

// Note: This is based on current CSS vars and would need to be updated if those change.
const containerWidth = 1344;

const intentSizes: Record<ImageSizeIntent, string> = {
  hero: '100vw',
  full: `(max-width: 48em) 100vw, ${containerWidth}px`,
  half: `(max-width: 40em) 100vw, (max-width: 48em) 50vw, ${containerWidth / 2}px`,
  third: `(max-width: 40em) 100vw, (max-width: 48em) 50vw, ${containerWidth / 3}px`,
  og: '800px',
};

export const optimizeContentfulImgSrc = (
  src: string,
  sizeIntent: ImageSizeIntent,
) => {
  if (!src) {
    return src;
  }

  const url = new URL(src);

  if (!url.hostname.includes('ctfassets')) {
    return src;
  }

  // sometimes things come from downloads which doesn't seem to support the query params
  // force these to the images url
  if (url.hostname.startsWith('downloads.ctfassets')) {
    url.hostname = url.hostname.replace(
      'downloads.ctfassets',
      'images.ctfassets',
    );
  }

  url.searchParams.set('w', intentSourceSizes[sizeIntent]);
  url.searchParams.set('q', '75');
  url.searchParams.set('fm', 'webp');

  return url.toString();
};

// Trial using a single generic blurred image for all images. Is this better than white background?
// Loading an actual blurred version of every image at build time is not feasible.
// Doing it on the client results in reduced overall performance.
// If we need this, we'll need to fetch it at publish time, store it in the DB, and return it in GraphQL (resulting in bloat).
// With no good solution for true blurred versions, perhaps a generic blur is the best compromise.
const blurDataURL =
  'data:image/webp;base64,UklGRsoKAABXRUJQVlA4WAoAAAAgAAAAPQMAKwIASUNDUMgBAAAAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADZWUDgg3AgAAPCSAJ0BKj4DLAI+7XKyVimlpCOgcOjhMB2JaW7hXR9YbSWcAoY9xPR/av44igX//cHcjS86fYAG8UqBsPQVaaqlHI7IKyGoGw9BVpqqUB6CrTVUojaqle9EXyq8mCyzP7RtRzYeh6X2QUbWLE8nYrO99wLVaNayOF8O6jLvEufaIicyLgJ+Ggc8AJ1eFkYGvZCdSAi2s2XfzcDyM154vCe4LSPMprJG5U2Y8ICDz1IItexarfhHI4GRQMRWITpe+aarJog0mh+JT+hnqr33Ulgp6r71wb4xiooE5HjjMt5pPSjNobZM4XjAVOdl3Ucvu34HNQROpAt0o6gX789W9cIYgxbq8XS8vUpUE3ToXfU5feun/759cG97Rb2dZ0nZnD/SJ+aIJM0APhZPGb2XlY5p9To//z64ODJJVnW2kiElxcv86IdNO8u89QQc27FWQnUr79TmPRwcjhf/xKf6KRvsMb2QnK3+3tVyIckL/pwl/2P8AXL/QZOgTSitXmo4znBBwhOo+Li5ljvpTpJyJltqs+9cJifLR2P+Nk533otXmoOZWeBOVc//p1jfYY32DB6cGLuCwglH4ze9cHF6dDFavNOj0WrzUSqODa52XlY3siStjlR383rg4lKVBC+xz/qnBhA0ei1eadHotXmmV2MWvwN0elX00J97IkrYYWW+bIjOVZ1r2dl1DQq9JuO7jegNOjvsJ2a0vVC/CZhHAhlaIUXLHjPQzMsE2O+pzCUpUDXPZ3CFTG9jEayf0rFozvceCJKTy2hW6Ml31EXthKCfTewQvZ/rnYJvxm98y3R6LYEvuv6Vijq9zR6XCJoh4eqNGXEW6h6ZWzspXbbKX4/jT3p1jtregNOkcOJuxpGGnR6LV6rN2x3gAtzNOkad9rlh2UGl+KC+uppY7Sae6cJ6LZsOusWi7rsQ01CUEC53pYjoVswzKSYthc4IOEJsiMXUgW6RmqM+66xjrrYpJaE/SCf8ryJc+tXpasYd7ITqQEXUgItfxHtzHIDTpHbZuZuWzVpqqUwKaE3G9Ah4w/16onTExdSAi6kA7dDtcrfLfZXzTVi110gOP0MxUlyi0uCklWQfeJY8Qd2ltOV7rQULmrTVUoD0F3SskG5fepS0c0sEwpAlAt1drTQSxms4HoKtNVSgPRizinUcH6xOZELDYenIqMXcLFx3N2HoKtNVSgPQVarc5MBAyKgoyVSTCGybCjacmXeZTVUoD0FWmqpQIzR/LJJroheqPXFPBLRm0nYegq01VKA9BVS/01dfWy4FDvv3+JJwyudBXc3w1pqqUB6CrTYu3jy02fFgVO0HQEV2QleOLduAOtVuVA2HoKtNVT8guatNWy5JT7yZuy4/f/FG1RwctNVSgPQVaarswMpqy7fDCCC0DKHt3rXlPjr7TzKaqlAegq7QYaBsQRg/lmD9Y8Pk4+c5NLPyUfOABVpqqUB6CsE2HoKtNWy5GHtAmwNmZqR9Lk+uQeD0FWmqpQLAfZBkoD0FW8niCLe1iaDijj0cRN2UYGU1VKA81fAHSeKqUB6Hk3FP3Hvr3eZbEIRmsFDsgq01VKBX5hnQAP6qn/nbNOaLL+hwZ8G8kdpW8x8+aQMcM5KCaq3M8/WFvlhglLOwWppRiO2GPhwJFbg3rnEBICq+RZSTina53BN77eIOkfBrlAuZobT4COyE4ciA4Vs44k+mbdnydCbJ4I73bewgcCzCDiCBu4ANZ/IQfn6EMl5eYVhi0NofQWFlaBpbbVdBsCT2GZ2BlvBijPxQ7T3y3Spp+9gkWiBCmBN6YWfPp59bQRFub8GHSUhKDKJ4XG4esBQyTcFeMlR2aK6WIBH22QFuiEhpH3Oh1vnB1oaRTzriqGG6NLglHwi1AmIq9s3WdtNg6Kmy1wf3lHlb3u8GIPdVpP5XayOZJPlxzMt6WKxXxzidppvHcJhCcGi985r+goDuFyW82K4z34/rNbUgvQLZTq3Z28+u4n7DuyBnNNHwcOALPpXgh4RW8KQH1a4pqSVk1FCwHGTWvPCutLnjeKIHu3IB7LzY02Rq1ngTCQrolpWcaE8M544T27K3MuuUUca+Go1XHflP8XLP0WGmfMyJILzsw89ZXNARBnuVwseAwU8ycO7m4fHsF9I7VH1u8+bm62QU6nQQZg8ZiaAXpnbn4UXdOMz5aYpuysO10j7cxww8asnM37qaVes2B+WrPe8EUpxUw2vCX+zRrRI63Sh8Ma6294/di2xfLPRcJs+5X4ZLoTmsLLbCgymAwdHLscZjAyUarVhjMXDJOz1O4xb46Mvw2kDZaNC8vpjhcODe3I2HWOG9EppDmreVEBBgBFHWdhvaM0OzzPrSg/Xh5eY/DWzIuXfBuzaL5QeemK4crxVCAJCfPWPUAMMkS5zD0sglTgBWHna7TpitupSnlDeHBEA3F11fdaAH5msWDVRereXTzj5CSx2zlVYF0jtkpnkOb0viFYj5Yr3CWpvCfOU26powf7kXNGtSWLvsNNRvbMnXkwgcBpHnSqc5jOblgTMI/I88jpE4ImVDUyI5GdLlf6vKTVW7cPPF6F0Zipznu3I9FchSu2JNik1lnwxQXgnrL4ZJudL0L3DxcwgAsSVQ15Z+p5jgE7hID3n/e2h7dso+31W8t0J4L+EEafCAAQ8oOuUQ2LFaqLLYhuft6nANO27tJWrlcnQJCgQAAr5Wjs5L+mprYOqDOT2Gs7DNqUJM5ZPgAACiMCeHovlgbJCslM8+20qOsAABp01r0n9WiPL5htDL2sC1CAAHEZKwFtWzEONBN3exENSnDzeAAOw2o9C5TjricaRYZv2AJ02CwAB2gTvcf26sW0YqzKYBHKiHQAAHyJYO9mFLHWO4Y5+3+oK7g8Fpv8R0AAomDi+xXZ/2QmPBTOF7Pxzglgan2vpyqpABD4/dfI1o4+EsxJbYUi706iinPXoAAC1iMreP9LMiojHFoxVqdXI4xAAafC3XUuIEDUoj6Q0IkQAcA7HpDLxBRZ2hNggzv7BwAL4PSIIAAA==';

type ImageProps = {
  image?: {
    url: string;
    alt?: null | string;
    ratio?: string | undefined;
    height?: number;
    width?: number;
    focus?: { x: string; y: string };
  };
  objectPosition?: string;
  objectFit?: 'cover' | 'contain';
  lazyLoad?: 'eager' | 'lazy';
  priority?: boolean;
  rounded?: string;
  aspectRatio?: string;
  aspectRatioSm?: string;
  aspectRatioMd?: string;
  aspectRatioLg?: string;
  className?: string;
  hasPlaceholder?: boolean;
  isVideoThumbnail?: boolean;
  videoDuration?: number;
  loader?: ImageLoader;
  sizes?: string;
  sizeIntent?: ImageSizeIntent;
};

export const Image: React.FC<ImageProps> = ({
  image,
  lazyLoad,
  priority,
  aspectRatio,
  aspectRatioSm,
  aspectRatioMd,
  aspectRatioLg,
  objectPosition = 'center',
  objectFit,
  rounded,
  className,
  hasPlaceholder = false,
  isVideoThumbnail = false,
  videoDuration,
  loader,
  sizes,
  sizeIntent = ImageSizeIntent.enum.full,
}) => {
  const width =
    aspectRatio === 'contain' && image?.width
      ? image.width
      : getAspectWidth(aspectRatio as string);
  const height =
    aspectRatio === 'contain' && image?.height
      ? image.height
      : getAspectHeight(aspectRatio as string);
  const isSvg = image?.url?.endsWith('.svg');

  if (!image && !hasPlaceholder) {
    return;
  }

  const shouldUseDimensions = aspectRatio || objectFit === 'contain';

  const imgProps =
    shouldUseDimensions && width && height ? { width, height } : { fill: true };

  return (
    <div
      className={cn(
        `Image`,
        {
          [`has-ratio`]:
            aspectRatio !== 'contain' &&
            (aspectRatio || aspectRatioMd || aspectRatioLg),
          [`aspect-ratio-${aspectRatio}`]: aspectRatio,
          [`@sm:aspect-ratio-${aspectRatioSm}`]: aspectRatioSm,
          [`@md:aspect-ratio-${aspectRatioMd}`]: aspectRatioMd,
          [`@lg:aspect-ratio-${aspectRatioLg}`]: aspectRatioLg,
          [`rounded-${rounded}`]: rounded,
        },
        className,
      )}
      style={
        !image
          ? {
              backgroundImage: `url(/placeholder.svg)`,
              backgroundSize: '20px',
            }
          : undefined
      }
    >
      {isVideoThumbnail && (
        <div className="Image__video-thumb">
          <div className="Image__video-thumb__overlay" />
          <div className="Image__video-thumb__info">
            {videoDuration && (
              <div className="label-xl label-potent p-comfy-lg">
                {formatDuration(videoDuration)}
              </div>
            )}
          </div>
          <div className="Image__video-thumb__play">
            <ArkIcon iconName="play" />
          </div>
        </div>
      )}
      {image && (
        <NextImage
          className={cn(
            'Image',
            getImageVariant(objectPosition as string, rounded as string),
            objectFit || (isSvg ? 'contain' : 'cover'),
            `${className}-img`,
          )}
          loading={priority ? 'eager' : lazyLoad}
          fetchPriority={priority ? 'high' : undefined}
          src={optimizeContentfulImgSrc(image?.url, sizeIntent)}
          alt={image?.alt as string}
          loader={loader}
          placeholder="blur"
          blurDataURL={blurDataURL}
          sizes={sizes || intentSizes[sizeIntent]}
          {...imgProps}
          style={{
            objectPosition: image.focus
              ? `${image.focus.x} ${image.focus.y}`
              : undefined,
          }}
        />
      )}
    </div>
  );
};
